import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useTranslation } from "react-i18next";
import Page from "components/page/Page";
import {
  DeleteFoodOrderingPickupLocation,
  GetFoodOrderingPickupLocation,
  GetFoodOrderingSettings,
  PostFoodOrderingPickupLocation,
  PostFoodOrderingSettings,
  PutFoodOrderingSettings,
  GetFoodOrderingCategories,
} from "api/rpc/2024-04/facilityAdmin/facility/foodOrdering";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import Card from "components/card/Card";
import Toggle from "components/form/toggle/Toggle";
import FormLayout from "components/form/FormLayout";
import { convertTime, unsavedChangesExist, valueToString } from "helpers/Helpers";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/Input";
import "./foodOrdering.scss";
import Sheet from "components/sheet/Sheet";
import { IRegister } from "../../Admin";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCashRegister } from "@fortawesome/pro-light-svg-icons";
import { ButtonNew as Button } from "components/buttonNew";
import TimePick from "components/timePick/TimePick";
import DataTable from "components/dataTable/DataTable";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { generateOrderStatusBadge } from "components/orderTable/OrdersTable";
import Tabs from "components/tabs/Tabs";
import CopyLink from "components/copyLink/CopyLink";
import moment from "moment";
import { OrderFinancialStatus } from "redux/reducers/models/order";

interface IFoodOrderingSettings {
  //Food Order Settings
  enabled: boolean;
  open_time: string;
  close_time: string;
  allow_off_course_orders: boolean;
  allow_on_course_orders: boolean;
  allow_on_course_deliveries: boolean;
  mark_orders_as_overtime: boolean;
  mark_orders_as_overtime_after_minutes: string;
  show_product_images: boolean;
  members_only: boolean;

  //POS Food Order Settings
  print_orders_in_kitchen: boolean;
  print_orders: boolean;
  print_order_details_in_kitchen: boolean;

  //Food Order Payment Settings
  pay_at_counter: boolean;
  pay_online: boolean;
  pay_on_account: boolean;

  //Food Order Notification Settings
  send_confirmation_text: boolean;
  send_prep_time_text: boolean;
  send_ready_text: boolean;

  //Contact Info
  contact_name: string;
  contact_phone: string;

  operating_hours: any[];
}

interface IFilterState {
  addLocationModalOpen: boolean;
  registersModal: boolean;
  registers: IRegister[];
  pickupLocations: any[];
  addOffCoursePickupLocation: boolean;
  addOnCoursePickupLocation: boolean;
  addDiningLocation: boolean;

  onCoursePickupLocationTitle: string;
  onCoursePickupLocation: string;
  offCoursePickupLocationTitle: string;
  offCoursePickupLocation: string;
  diningLocationTitle: string;
  diningLocation: string;
  openOrderHistory: boolean;
  foodOrders: any[];

  categories: any[];
}

export default function FoodOrderingSettings() {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const facilityStore = useAppSelector(store => store.facilityStore);
  console.log("Facility Store", facilityStore);

  const [foodOrderingSettings, setFoodOrderingSettings] = useState<IFoodOrderingSettings>({
    //Food Order Settings
    enabled: false,
    open_time: "",
    close_time: "",
    allow_on_course_deliveries: false,
    allow_off_course_orders: false,
    allow_on_course_orders: false,
    mark_orders_as_overtime: false,
    mark_orders_as_overtime_after_minutes: "",
    show_product_images: false,
    members_only: false,

    //POS Food Order Settings
    print_orders_in_kitchen: false,
    print_orders: false,
    print_order_details_in_kitchen: false,

    //Food Order Notification Settings
    send_confirmation_text: false,
    send_prep_time_text: false,
    send_ready_text: false,

    //Food Order Payment Settings
    pay_at_counter: false,
    pay_on_account: false,
    pay_online: false,

    //Contact Info
    contact_name: "",
    contact_phone: "",

    operating_hours: [],
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    addLocationModalOpen: false,
    registersModal: false,
    registers: [],
    pickupLocations: [],

    addOffCoursePickupLocation: false,
    addOnCoursePickupLocation: false,
    addDiningLocation: false,

    offCoursePickupLocationTitle: "",
    offCoursePickupLocation: "",
    onCoursePickupLocationTitle: "",
    onCoursePickupLocation: "",
    diningLocationTitle: "",
    diningLocation: "",
    openOrderHistory: false,
    foodOrders: undefined,

    categories: [],
  });

  const [foodOrderingSettingsBeforeChanges, setFoodOrderingSettingsBeforeChanges] =
    useState<IFoodOrderingSettings>(foodOrderingSettings);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const tabs = [
    {
      id: "general",
      content: "General",
      panelID: "food-ordering-content-1",
    },
    {
      id: "categories",
      content: "Categories",
      panelID: "food-ordering-content-2",
    },
    {
      id: "pickup-locations",
      content: "Pickup Locations",
      panelID: "food-ordering-content-3",
    },
    {
      id: "order-history",
      content: "Orders",
      panelID: "food-ordering-content-4",
    },
  ];

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadFoodOrderingSettings();
    return () => source.cancel();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (selectedTab === 1) {
      void loadFoodOrderingCategories(source.token);
    }
    if (selectedTab === 2) {
      void loadPickupLocations(source.token);
    }
    if (selectedTab === 3) {
      void handleOpenOrderHistory();
    }
    return () => source.cancel();
  }, [selectedTab]);

  async function loadFoodOrderingSettings() {
    const foodOrderingRes = await GetFoodOrderingSettings(null, true);

    //Settings not set - call POST
    if (foodOrderingRes?.data === "") {
      const initialParams = {
        enabled: false,
        allow_off_course_orders: false,
        allow_on_course_deliveries: false,
        allow_on_course_orders: false,
        mark_orders_as_overtime: false,
        mark_orders_as_overtime_after_minutes: 15,
        members_only: false,
        show_product_images: false,
        print_order_details_in_kitchen: false,
        print_orders: false,
        print_orders_in_kitchen: false,
        send_confirmation_text: false,
        send_prep_time_text: false,
        send_ready_text: false,
        pay_at_counter: false,
        pay_on_account: false,
        pay_online: false,
        contact_name: "",
        contact_phone: "",
        open_time: "06:00",
        close_time: "19:00",
      };

      const initialFoodOrderRes = await PostFoodOrderingSettings(initialParams, true);

      if (initialFoodOrderRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error creating settings")));
        return;
      }

      setFoodOrderingSettings(prevState => ({
        ...prevState,
        enabled: false,
        allow_off_course_orders: false,
        allow_on_course_deliveries: false,
        allow_on_course_orders: false,
        mark_orders_as_overtime: false,
        mark_orders_as_overtime_after_minutes: "",
        members_only: false,
        show_product_images: false,
        print_order_details_in_kitchen: false,
        print_orders: false,
        print_orders_in_kitchen: false,
        send_confirmation_text: false,
        send_prep_time_text: false,
        send_ready_text: false,
        pay_at_counter: false,
        pay_on_account: false,
        pay_online: false,
        contact_name: "",
        contact_phone: "",
        open_time: "06:00",
        close_time: "19:00",
      }));

      setFoodOrderingSettingsBeforeChanges(prevState => ({
        ...prevState,
        enabled: false,
        allow_off_course_orders: false,
        allow_on_course_deliveries: false,
        allow_on_course_orders: false,
        mark_orders_as_overtime: false,
        mark_orders_as_overtime_after_minutes: "",
        members_only: false,
        show_product_images: false,
        print_order_details_in_kitchen: false,
        print_orders: false,
        print_orders_in_kitchen: false,
        send_confirmation_text: false,
        send_prep_time_text: false,
        send_ready_text: false,
        pay_at_counter: false,
        pay_on_account: false,
        pay_online: false,
        contact_name: "",
        contact_phone: "",
        open_time: "06:00",
        close_time: "19:00",
      }));

      return;
    }

    if (foodOrderingRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading settings")));
      return;
    }

    const foodOrderingSettings: IFoodOrderingSettings = foodOrderingRes.data;

    setFoodOrderingSettings(prevState => ({
      ...prevState,
      enabled: !!foodOrderingRes.data.enabled,
      allow_off_course_orders: !!foodOrderingSettings?.allow_off_course_orders,
      allow_on_course_deliveries: !!foodOrderingSettings?.allow_on_course_deliveries,
      allow_on_course_orders: !!foodOrderingSettings?.allow_on_course_orders,
      mark_orders_as_overtime: !!foodOrderingSettings?.mark_orders_as_overtime,
      mark_orders_as_overtime_after_minutes: foodOrderingSettings?.mark_orders_as_overtime_after_minutes,
      members_only: foodOrderingSettings?.members_only,
      show_product_images: foodOrderingSettings?.show_product_images,
      print_order_details_in_kitchen: !!foodOrderingSettings?.print_order_details_in_kitchen,
      print_orders: !!foodOrderingSettings?.print_orders,
      print_orders_in_kitchen: !!foodOrderingSettings?.print_orders_in_kitchen,
      send_confirmation_text: !!foodOrderingSettings?.send_confirmation_text,
      send_prep_time_text: !!foodOrderingSettings?.send_prep_time_text,
      send_ready_text: !!foodOrderingSettings?.send_ready_text,
      pay_at_counter: !!foodOrderingSettings?.pay_at_counter,
      pay_on_account: !!foodOrderingSettings?.pay_on_account,
      pay_online: !!foodOrderingSettings?.pay_online,
      contact_name: foodOrderingSettings?.contact_name ? foodOrderingSettings?.contact_name : "",
      contact_phone: foodOrderingSettings?.contact_phone ? foodOrderingSettings?.contact_phone : "",
      open_time: foodOrderingSettings?.open_time,
      close_time: foodOrderingSettings?.close_time,
      operating_hours: foodOrderingSettings?.operating_hours,
    }));

    setFoodOrderingSettingsBeforeChanges(prevState => ({
      ...prevState,
      enabled: !!foodOrderingRes.data.enabled,

      allow_off_course_orders: !!foodOrderingSettings?.allow_off_course_orders,
      allow_on_course_deliveries: !!foodOrderingSettings?.allow_on_course_deliveries,
      allow_on_course_orders: !!foodOrderingSettings?.allow_on_course_orders,
      mark_orders_as_overtime: !!foodOrderingSettings?.mark_orders_as_overtime,
      mark_orders_as_overtime_after_minutes: foodOrderingSettings?.mark_orders_as_overtime_after_minutes,
      members_only: foodOrderingSettings?.members_only,
      show_product_images: foodOrderingSettings?.show_product_images,
      print_order_details_in_kitchen: !!foodOrderingSettings?.print_order_details_in_kitchen,
      print_orders: !!foodOrderingSettings?.print_orders,
      print_orders_in_kitchen: !!foodOrderingSettings?.print_orders_in_kitchen,
      send_confirmation_text: !!foodOrderingSettings?.send_confirmation_text,
      send_prep_time_text: !!foodOrderingSettings?.send_prep_time_text,
      send_ready_text: !!foodOrderingSettings?.send_ready_text,
      pay_at_counter: !!foodOrderingSettings?.pay_at_counter,
      pay_on_account: !!foodOrderingSettings?.pay_on_account,
      pay_online: !!foodOrderingSettings?.pay_online,
      contact_name: foodOrderingSettings?.contact_name ? foodOrderingSettings?.contact_name : "",
      contact_phone: foodOrderingSettings?.contact_phone ? foodOrderingSettings?.contact_phone : "",
      open_time: foodOrderingSettings?.open_time,
      close_time: foodOrderingSettings?.close_time,
      operating_hours: foodOrderingSettings?.operating_hours,
    }));
  }

  function handleToggleChange(event: ChangeEvent<HTMLInputElement>) {
    const id = event?.target?.id;
    const checked = event?.target?.checked;

    if (id === undefined || checked === undefined) {
      return;
    }

    setFoodOrderingSettings(prev => {
      return { ...prev, [id]: checked };
    });
  }

  async function saveFoodOrderingSettings() {
    const updateSettingsRes = await PutFoodOrderingSettings(foodOrderingSettings, true);

    if (updateSettingsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving settings")));
      return;
    }

    setFoodOrderingSettingsBeforeChanges(foodOrderingSettings);
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;

    if (id === undefined || checked === undefined) {
      return;
    }

    setFoodOrderingSettings(prev => {
      return { ...prev, [id]: checked };
    });
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setFoodOrderingSettings(prevState => ({ ...prevState, [id]: value }));
  }

  function handlePickupLocationInputChange(event: any) {
    const { id, value } = event.target;
    setFilterState(prevState => ({ ...prevState, [id]: value }));
  }

  function sendToPage(address: string, openNewTab = false) {
    if (window.location.search.includes(address)) {
      return;
    } else if (openNewTab) {
      window.open(address);
      return;
    }
    history.push(address);
  }

  async function loadPickupLocations(token?: CancelToken) {
    const pickupLocationRes = await GetFoodOrderingPickupLocation(null, true, token);

    if (pickupLocationRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting pickup locations")));
      return;
    }

    setFilterState(prevState => ({ ...prevState, pickupLocations: pickupLocationRes.data }));
  }

  async function loadFoodOrderingCategories(token?: CancelToken) {
    const categoryRes = await GetFoodOrderingCategories(null, true, token);

    if (token && token.reason) {
      return;
    }
    if (categoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading settings")));
      return;
    }

    setFilterState(prevState => ({ ...prevState, categories: categoryRes.data }));
  }

  async function handleAddOffCourseLocation() {
    const params = {
      type: "off_course",
      name: filterState.offCoursePickupLocationTitle,
      subtitle: filterState.offCoursePickupLocation,
    };

    const addPickupLocationRes = await PostFoodOrderingPickupLocation(params, true);

    if (addPickupLocationRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating pickup location")));
      return;
    }

    void loadPickupLocations();
    setFilterState(prevState => ({
      ...prevState,
      offCoursePickupLocation: "",
      offCoursePickupLocationTitle: "",
      addOffCoursePickupLocation: false,
    }));
  }

  async function handleAddOnCourseLocation() {
    const params = {
      type: "on_course",
      name: filterState.onCoursePickupLocationTitle,
      subtitle: filterState.onCoursePickupLocation,
    };

    const addPickupLocationRes = await PostFoodOrderingPickupLocation(params, true);

    if (addPickupLocationRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating pickup location")));
      return;
    }

    void loadPickupLocations();
    setFilterState(prevState => ({
      ...prevState,
      onCoursePickupLocation: "",
      onCoursePickupLocationTitle: "",
      addOnCoursePickupLocation: false,
    }));
  }

  async function handleAddDiningLocation() {
    const params = {
      type: "dining",
      name: filterState.diningLocationTitle,
      subtitle: filterState.diningLocation,
    };

    const addPickupLocationRes = await PostFoodOrderingPickupLocation(params, true);

    if (addPickupLocationRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating pickup location")));
      return;
    }

    void loadPickupLocations();
    setFilterState(prevState => ({
      ...prevState,
      diningLocation: "",
      diningLocationTitle: "",
      addDiningLocation: false,
    }));
  }

  function handleShowOffLocationInputs() {
    if (filterState.addOffCoursePickupLocation) {
      setFilterState(prevState => ({
        ...prevState,
        offCoursePickupLocation: "",
        offCoursePickupLocationTitle: "",
      }));
    }

    setFilterState(prevState => ({
      ...prevState,
      addOffCoursePickupLocation: !filterState?.addOffCoursePickupLocation,
    }));
  }

  function handleShowOnLocationInputs() {
    if (filterState.addOnCoursePickupLocation) {
      setFilterState(prevState => ({
        ...prevState,
        onCoursePickupLocation: "",
        onCoursePickupLocationTitle: "",
      }));
    }

    setFilterState(prevState => ({
      ...prevState,
      addOnCoursePickupLocation: !filterState?.addOnCoursePickupLocation,
    }));
  }

  function handleShowDiningInputs() {
    if (filterState.addDiningLocation) {
      setFilterState(prevState => ({
        ...prevState,
        diningLocation: "",
        diningLocationTitle: "",
      }));
    }

    setFilterState(prevState => ({
      ...prevState,
      addDiningLocation: !filterState?.addDiningLocation,
    }));
  }

  async function handleRemovePickupLocation(locationId: string) {
    const deleteLocationRes = await DeleteFoodOrderingPickupLocation({ id: locationId }, true);

    if (deleteLocationRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting pickup location")));
      return;
    }

    void loadPickupLocations();
  }

  async function handleOpenOrderHistory() {
    // const historyRes = await GetFoodOrderingOrders({ order: true }, true);
    const historyRes = await GetOrder({ food_orders: true }, true);

    if (historyRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading order history")));
      return;
    }

    setFilterState(prevState => ({ ...prevState, openOrderHistory: true, foodOrders: historyRes.data }));
  }

  function handleUpdateOperatingHours(index: number, columnId: string, value: any) {
    const newOperatingHours = [...foodOrderingSettings.operating_hours];

    newOperatingHours[index] = { ...newOperatingHours[index], [columnId]: value };

    setFoodOrderingSettings(prevState => ({ ...prevState, operating_hours: newOperatingHours }));
  }

  return (
    <Page
      title={t("Food Ordering Overview")}
      subtitle={t("Edit details about your online food ordering program")}
      notificationBarProps={{
        isVisible: unsavedChangesExist(foodOrderingSettings, foodOrderingSettingsBeforeChanges),
        onAction: () => saveFoodOrderingSettings(),
        onCancel: () => setFoodOrderingSettings(foodOrderingSettingsBeforeChanges),
      }}
    >
      <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} fullWidth />
      {selectedTab === 0 ? (
        <Card>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <div>
                  <p className="food-ordering-settings-title">{t("Food Ordering")}</p>
                  <p className="food-ordering-settings-subtitle">
                    {t("Turn on and off food ordering to receive orders")}
                  </p>{" "}
                </div>
                <div className="flex justify-end">
                  <Toggle
                    label={t("Enabled")}
                    id="enabled"
                    checked={foodOrderingSettings.enabled}
                    onChange={handleToggleChange}
                  />
                </div>
              </FormLayout.Group>
              {foodOrderingSettings.enabled ? (
                <FormLayout.Group>
                  <CopyLink
                    label={t("Customers can access food ordering using the following link:")}
                    link={`${window.origin}/portal/${facilityStore.facility?.client?.short_name}/food-ordering/menu/${facilityStore.facility?.short_name}`}
                  />
                </FormLayout.Group>
              ) : null}
            </FormLayout>
          </Card.Section>

          <Card.Section >
            <div className="flex items-start">
              <div className="w-1/2">
                <h1 className="food-ordering-settings-setting-title">{"Operating Hours"}</h1>
              </div>
              <div className="w-full">
                <div className="food-ordering_operating_hours_container">
                  {foodOrderingSettings?.operating_hours?.map((hour, index) => (
                    <div key={index} className="food-ordering_operating_hours_row">
                      <div className="food-ordering_operating_hours_day">{hour.day_of_week}</div>
                      <Toggle
                        id="allow_off_course_orders"
                        checked={hour.closed}
                        onChange={e => handleUpdateOperatingHours(index, "closed", e.target.checked)}
                        label={t("Closed")}
                      />
                      {!hour.closed ? (
                        <div className="flex items-center gap-4 w-1/4">
                          <TimePick
                            value={hour.open_time}
                            onChange={timeString => handleUpdateOperatingHours(index, "open_time", timeString)}
                            size="medium"
                            align="right"
                            disabled={hour.closed}
                          />

                          <div className="food-ordering_operating_hours_day">{"-"}</div>

                          <TimePick
                            value={hour.close_time}
                            onChange={timeString => handleUpdateOperatingHours(index, "close_time", timeString)}
                            size="medium"
                            align="right"
                            disabled={hour.closed}
                          />
                        </div>
                      ) : null }
                      
                    </div>
                    ))}
                </div>
              </div>
            </div>
          </Card.Section>

          <Card.Section>
            <div className="flex items-start">
              <div className="w-1/2">
                <h1 className="food-ordering-settings-setting-title">{"Order Settings"}</h1>
              </div>

              <div className="w-full">
                <div>

                  <Checkbox
                    id="allow_off_course_orders"
                    size="medium"
                    checked={foodOrderingSettings.allow_off_course_orders}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Allow orders from off-course locations")}
                  />

                  <Checkbox
                    id="allow_on_course_orders"
                    size="medium"
                    checked={foodOrderingSettings.allow_on_course_orders}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Allow orders from on-course locations")}
                  />

                  <Checkbox
                    id="allow_on_course_deliveries"
                    size="medium"
                    checked={foodOrderingSettings.allow_on_course_deliveries}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Allow orders to be delivered on the course")}
                  />

                  <Checkbox
                    id="mark_orders_as_overtime"
                    size="medium"
                    checked={foodOrderingSettings.mark_orders_as_overtime}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Mark orders as overtime")}
                  />

                  {foodOrderingSettings.mark_orders_as_overtime && (
                    <div className="flex items-center mb-2 gap-4 w-1/3">
                      <p>{t("After ")}</p>
                      <div>
                        <Input
                          value={foodOrderingSettings.mark_orders_as_overtime_after_minutes}
                          id="mark_orders_as_overtime_after_minutes"
                          onChange={handleInputChange}
                          placeholder={t("10 mins...")}
                          labelHidden
                          suffix={t("Minutes")}
                        />
                      </div>
                    </div>
                  )}

                  <Checkbox
                    id="show_product_images"
                    size="medium"
                    checked={foodOrderingSettings.show_product_images}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Show product images")}
                  />
                  <Checkbox
                    id="print_orders_in_kitchen"
                    size="medium"
                    checked={foodOrderingSettings.print_orders_in_kitchen}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Print online orders to the kitchen from this computer (Printer App required)")}
                  />
                  <Checkbox
                    id="members_only"
                    size="medium"
                    checked={foodOrderingSettings.members_only}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Restrict food orders to members only")}
                  />
                </div>
              </div>
            </div>
          </Card.Section>

          <Card.Section>
            <div className="flex items-start">
              <div className="w-1/2">
                <h1 className="food-ordering-settings-setting-title">{t("Food order payment settings")}</h1>{" "}
              </div>

              <div className="w-full">
                <div>
                  <Checkbox
                    id="pay_online"
                    size="medium"
                    checked={foodOrderingSettings.pay_online}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Allow online payment")}
                  />

                  <Checkbox
                    id="pay_at_counter"
                    size="medium"
                    checked={foodOrderingSettings.pay_at_counter}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Allow pay at counter")}
                  />

                  <Checkbox
                    id="pay_on_account"
                    size="medium"
                    checked={foodOrderingSettings.pay_on_account}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Allow on account purchases for online orders")}
                  />
                </div>
              </div>
            </div>
          </Card.Section>

          <Card.Section>
            <div className="flex items-start">
              <div className="w-1/2">
                <h1 className="food-ordering-settings-setting-title">{t("Food order notifications")}</h1>{" "}
              </div>

              <div className="w-full">
                <div>
                  <Checkbox
                    id="send_confirmation_text"
                    size="medium"
                    checked={foodOrderingSettings.send_confirmation_text}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Send confirmation text to customers")}
                  />

                  <Checkbox
                    id="send_ready_text"
                    size="medium"
                    checked={foodOrderingSettings.send_ready_text}
                    onChange={handleCheckboxChange}
                    fullWidth
                    label={t("Send text when order is ready")}
                  />
                </div>
              </div>
            </div>
          </Card.Section>
        </Card>
      ) : null}

      {selectedTab === 1 ? (
        <Card title={t("Categories")} subtitle={t("Add and manage food ordering categories")} titleActions={[{ 
          content: t("+ Add category"),
          action: () => history.push("/admin/food-ordering/categories/new"),
          buttonType: "default",
        }]}>
          <Card.Section table>
            <DataTable
              columns={[{ label: t("Title") }]}
              loading={filterState.categories === undefined}
            >
              {filterState.categories?.length > 0 ? (
                filterState.categories?.map((category: any, index: number) => {
                  return (
                    <tr
                      key={index}
                      onClick={() => history.push("/admin/food-ordering/categories/" + String(category.id))}
                      className="clickable"
                    >
                      <td>
                        <p>{category.title}</p>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td>{t("There are currently no categories")}</td>
                </tr>
              )}
            </DataTable>
          </Card.Section>
        </Card>
      ) : null}

      {selectedTab === 2 ? (
        <div>
          <p className="food-ordering-settings-setting-title">{t("Off-course pickup location")}</p>
          <p className="food-ordering-settings-setting-subtitle">{t("Add your off course takeout locations")}</p>{" "}
          {filterState?.pickupLocations
            .filter(location => location.type === "off_course")
            .map((location: any, index: number) => {
              return (
                <div key={index} className="food-ordering-settings-pickup-container">
                  <div>
                    <p className="food-ordering-settings-staff-name">{location.name}</p>
                    <p className="food-ordering-settings-staff-position">{location.subtitle}</p>
                  </div>

                  <div
                    onClick={() => handleRemovePickupLocation(String(location.id))}
                    className="food-ordering-settings-remove-button"
                  >
                    Remove
                  </div>
                </div>
              );
            })}

          {filterState?.addOffCoursePickupLocation && (
            <div>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    onChange={handlePickupLocationInputChange}
                    value={filterState?.offCoursePickupLocationTitle}
                    id="offCoursePickupLocationTitle"
                    labelHidden
                    placeholder={t("Name")}
                  />

                  <Input
                    onChange={handlePickupLocationInputChange}
                    value={filterState?.offCoursePickupLocation}
                    id="offCoursePickupLocation"
                    labelHidden
                    placeholder={t("Location")}
                  />
                </FormLayout.Group>
              </FormLayout>

              <div className="flex justify-end">
                <Button
                  type="text"
                  size="small"
                  className=""
                  disabled={
                    filterState?.offCoursePickupLocation === "" || filterState?.offCoursePickupLocationTitle === ""
                  }
                  onClick={handleAddOffCourseLocation}
                >
                  {"+ Add Location"}
                </Button>
              </div>
            </div>
          )}
          <div>
            <Button type="text" size="small" className="" onClick={handleShowOffLocationInputs}>
              {filterState?.addOffCoursePickupLocation ? "Close" : "+ Add"}
            </Button>
          </div>


          <hr />
          <p className="food-ordering-settings-setting-title">{t("On-course pickup location")}</p>
          <p className="food-ordering-settings-setting-subtitle">{t("Add your on course pickup locations")}</p>{" "}
          {filterState?.pickupLocations
            .filter(location => location.type === "on_course")
            .map((location: any, index: number) => {
              return (
                <div key={index} className="food-ordering-settings-pickup-container">
                  <div>
                    <p className="food-ordering-settings-staff-name">{location.name}</p>
                    <p className="food-ordering-settings-staff-position">{location.subtitle}</p>
                  </div>

                  <div
                    onClick={() => handleRemovePickupLocation(String(location.id))}
                    className="food-ordering-settings-remove-button"
                  >
                    Remove
                  </div>
                </div>
              );
            })}
          {filterState?.addOnCoursePickupLocation && (
            <div>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    onChange={handlePickupLocationInputChange}
                    value={filterState?.onCoursePickupLocationTitle}
                    id="onCoursePickupLocationTitle"
                    labelHidden
                    placeholder={t("Name")}
                  />

                  <Input
                    onChange={handlePickupLocationInputChange}
                    value={filterState?.onCoursePickupLocation}
                    id="onCoursePickupLocation"
                    labelHidden
                    placeholder={t("Location")}
                  />
                </FormLayout.Group>
              </FormLayout>

              <div className="flex justify-end">
                <Button
                  type="text"
                  size="small"
                  className=""
                  disabled={
                    filterState?.onCoursePickupLocation === "" || filterState?.onCoursePickupLocationTitle === ""
                  }
                  onClick={handleAddOnCourseLocation}
                >
                  {t("+ Add Location")}
                </Button>
              </div>
            </div>
          )}
          <div>
            <Button type="text" size="small" className="" onClick={handleShowOnLocationInputs}>
              {filterState?.addOnCoursePickupLocation ? t("Close") : t("+ Add")}
            </Button>
          </div>
          <hr />
          <p className="food-ordering-settings-setting-title">{t("Dining locations")}</p>
          <p className="food-ordering-settings-setting-subtitle">{t("Add your dining locations")}</p>{" "}
          {/* Dining Locations */}
          {filterState?.pickupLocations
            .filter(location => location.type === "dining")
            .map((location: any, index: number) => {
              return (
                <div key={index} className="food-ordering-settings-pickup-container">
                  <div>
                    <p className="food-ordering-settings-staff-name">{location.name}</p>
                    <p className="food-ordering-settings-staff-position">{location.subtitle}</p>
                  </div>

                  <div
                    onClick={() => handleRemovePickupLocation(String(location.id))}
                    className="food-ordering-settings-remove-button"
                  >
                    Remove
                  </div>
                </div>
              );
            })}

          {filterState?.addDiningLocation && (
            <div>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    onChange={handlePickupLocationInputChange}
                    value={filterState?.diningLocationTitle}
                    id="diningLocationTitle"
                    labelHidden
                    placeholder={t("Name")}
                  />

                  <Input
                    onChange={handlePickupLocationInputChange}
                    value={filterState?.diningLocation}
                    id="diningLocation"
                    labelHidden
                    placeholder={t("Location")}
                  />
                </FormLayout.Group>
              </FormLayout>

              <div className="flex justify-end">
                <Button
                  type="text"
                  size="small"
                  className=""
                  disabled={
                    filterState?.diningLocation === "" || filterState?.diningLocationTitle === ""
                  }
                  onClick={handleAddDiningLocation}
                >
                  {t("+ Add Location")}
                </Button>
              </div>
            </div>
          )}
          <div>
            <Button type="text" size="small" className="" onClick={handleShowDiningInputs}>
              {filterState?.addDiningLocation ? t("Close") : t("+ Add")}
            </Button>
          </div>
        </div>
      ) : null}

      {selectedTab === 3 ? (
        <div>
          <DataTable
            columns={[
              { label: t("Name") },
              { label: t("Date") },
              { label: t("Pickup Time") },
              { label: t("Pickup Type") },
              { label: t("Financial Status") },
              { label: "" },
            ]}
            loading={filterState.foodOrders === undefined}
          >
            {filterState?.foodOrders?.map((order: any, index: number) => {
              return (
                <tr key={index}>
                  <td>{order.name}</td>
                  <td>{moment(String(order.created_at)).format("LLL")}</td>
                  {/* <td>{moment(order.pickup_time).format("LT")}</td> */}
                  <td>{convertTime(order.pickup_time)}</td>
                  <td>{valueToString(String(order.pickup_type))}</td>
                  <td>{generateOrderStatusBadge(order.financial_status as OrderFinancialStatus)}</td>

                  <td
                    onClick={() => sendToPage("/admin/order/" + String(order.id), true)}
                    className="flex justify-center"
                  >
                    <div className="cursor-pointer customer-profile" style={{ textDecorationLine: "none" }}>
                      {t("View")}
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </div>
      ) : null}

      {/* Register */}
      <Sheet
        open={filterState?.registersModal}
        title={t("Registers")}
        supportText={t("Registers that print to kitchen")}
        size="small"
        onCancel={() => setFilterState(prevState => ({ ...prevState, registersModal: false }))}
        cancelText={t("Cancel")}
        okText={t("Confirm")}
      >
        <div>
          {filterState?.registers?.map((register: IRegister, index: number) => {
            return (
              <div
                onClick={() => sendToPage("/admin/settings/registers/" + String(register.id), true)}
                className="food-ordering-registers-container"
                key={index}
              >
                <div>
                  <FontAwesomeIcon
                    size="1x"
                    fixedWidth
                    icon={faCashRegister}
                    className="food-ordering-registers-container_register_icon"
                  />
                </div>
                <div className="food-ordering-registers-container_register_title">{register.title}</div>
              </div>
            );
          })}
        </div>
      </Sheet>
    </Page>
  );
}
